import * as React from 'react'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'
import { FieldsetInput } from '../../components/FieldsetInput'

import { IntegrationsConfigurationModel } from '@/lib/api/company/configuration/integration/types'
import { usePersistentTab } from '@/modules/shared/stores/tabsStore'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  isSaving: boolean
  state: IntegrationsConfigurationModel
  setState: React.Dispatch<React.SetStateAction<IntegrationsConfigurationModel>>
}

export const IntegrationForm = ({ isSaving, state, setState }: Props) => {
  const { t } = useLocalize()
  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, type, checked } = e.target

      if (!name) {
        return
      }

      if (name.includes('.')) {
        const [parentName, childName] = name.split('.')
        setState((prevState) => {
          const newState = structuredClone(prevState)
          switch (type) {
            case 'number':
              newState[parentName][childName] = Number(value)
              break
            case 'checkbox':
              newState[parentName][childName] = Boolean(checked)
              break
            default:
              newState[parentName][childName] = String(value)
              break
          }

          return newState
        })
      }
    },
    [setState],
  )

  const [selectedTab, setSelectedTab] = usePersistentTab(
    'settings-integrations',
    'imi',
  )

  return (
    <SettingsSectionWithTabs
      selected={selectedTab}
      setSelected={setSelectedTab}
    >
      <div
        data-tab-name="imi"
        title="IMI"
        className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
      >
        {state.imi ? (
          <>
            <FieldsetInput
              label={t('configuration-integration.username')}
              value={state.imi.login}
              name="imi.login"
              placeholder="np. jan-kowalski"
              onChange={handleInputChange}
              disabled={isSaving}
              type="text"
            />

            <FieldsetInput
              label={t('configuration-integration.password')}
              value={state.imi.password}
              name="imi.password"
              placeholder="np. ***"
              onChange={handleInputChange}
              disabled={isSaving}
              type="password"
            />

            <FieldsetInput
              label={t('common.company')}
              value={state.imi.company}
              name="imi.company"
              placeholder="np. Xyz Logistic"
              onChange={handleInputChange}
              disabled={isSaving}
              type="text"
            />

            <FieldsetInput
              label="Operator ID"
              value={state.imi.operatorId}
              name="imi.operatorId"
              placeholder="np. 123-456-789"
              onChange={handleInputChange}
              disabled={isSaving}
              type="text"
            />

            <FieldsetInput
              label="API Key"
              value={state.imi.apiKey}
              name="imi.apiKey"
              placeholder="np. abc-def-ghi"
              onChange={handleInputChange}
              disabled={isSaving}
              type="text"
            />
          </>
        ) : (
          <p>{t('configuration-integration.imi-api-error')}</p>
        )}
      </div>
      <div
        data-tab-name="zus"
        title="ZUS"
        className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
      >
        {state.zus ? (
          <>
            <FieldsetInput
              label={t('configuration-integration.username')}
              value={state.zus.login}
              name="zus.login"
              placeholder="np. janKowalski"
              onChange={handleInputChange}
              disabled={isSaving}
              type="text"
            />

            <FieldsetInput
              label={t('configuration-integration.password')}
              value={state.zus.password}
              name="zus.password"
              placeholder="np. ***"
              onChange={handleInputChange}
              disabled={isSaving}
              type="password"
            />

            <FieldsetInput
              label={t('configuration-integration.zip-password')}
              value={state.zus.zipPassword}
              name="zus.zipPassword"
              placeholder="np. ***"
              onChange={handleInputChange}
              disabled={isSaving}
              type="password"
            />

            <FieldsetInput
              label={t('configuration-integration.company-nip')}
              value={state.zus.nip}
              name="zus.nip"
              placeholder="np. 123-456-789"
              onChange={handleInputChange}
              disabled={isSaving}
              type="text"
            />
          </>
        ) : (
          <p>{t('configuration-integration.zus-api-error')}</p>
        )}
      </div>
      <div
        data-tab-name="webfleet"
        title="Webfleet"
        className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
      >
        {state.webfleet ? (
          <>
            <FieldsetInput
              label={t('configuration-integration.webfleet-account-name')}
              value={state.webfleet.accountName ?? ''}
              name="webfleet.accountName"
              placeholder="np. TransportCompanyLtd"
              onChange={handleInputChange}
              disabled={isSaving}
              type="text"
            />

            <FieldsetInput
              label={t('configuration-integration.username')}
              value={state.webfleet.login ?? ''}
              name="webfleet.login"
              placeholder="np. admin.webfleet"
              onChange={handleInputChange}
              disabled={isSaving}
              type="text"
            />

            <FieldsetInput
              label={t('configuration-integration.password')}
              value={state.webfleet.password ?? ''}
              name="webfleet.password"
              placeholder="np. ***"
              onChange={handleInputChange}
              disabled={isSaving}
              type="password"
            />

            <FieldsetInput
              label={t('configuration-integration.webfleet-registration-prefix')}
              value={state.webfleet.registrationPrefix ?? ''}
              name="webfleet.registrationPrefix"
              placeholder="np. -"
              onChange={handleInputChange}
              disabled={isSaving}
              type="text"
            />
          </>
        ) : (
          <p>{t('configuration-integration.webfleet-api-error')}</p>
        )}
      </div>
    </SettingsSectionWithTabs>
  )
}
